import React from "react";
import { useAppSelector } from "@/app/hooks";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { Trade } from "@/api/fetchCreateUpdateResourceMetadata/types";

interface TradeSelectorProps {
  value?: number[];
  onChange: (tradeIds: number[]) => void;
  label?: string;
  className?: string;
}

export function TradeSelector({
  value = [],
  onChange,
  label = "Restrict to Trades",
  className = "",
}: TradeSelectorProps) {
  const { trades } = useAppSelector(
    (state) => state.resourceManagementMetadataReducer,
  );

  const handleAddTrade = (tradeId: string) => {
    const id = parseInt(tradeId, 10);
    if (!value.includes(id)) {
      onChange([...value, id]);
    }
  };

  const handleRemoveTrade = (tradeId: number) => {
    onChange(value.filter((id) => id !== tradeId));
  };

  const selectedTrades = trades.filter((trade: Trade) =>
    value.includes(trade.id),
  );
  const availableTrades = trades.filter(
    (trade: Trade) => !value.includes(trade.id),
  );

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <Label className="text-sm font-medium">
          {label}
          <span className="text-xs text-gray-500 ml-2">
            (Leave empty to show for all companies)
          </span>
        </Label>
      )}

      {/* Selected trades */}
      {selectedTrades.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedTrades.map((trade: Trade) => (
            <Badge
              className="flex items-center gap-1"
              key={trade.id}
              variant="secondary"
            >
              {trade.name}
              <button
                aria-label={`Remove ${trade.name}`}
                className="ml-1 hover:text-destructive"
                onClick={() => handleRemoveTrade(trade.id)}
                type="button"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Trade selector */}
      {availableTrades.length > 0 && (
        <Select onValueChange={handleAddTrade} value="">
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Add trade restriction..." />
          </SelectTrigger>
          <SelectContent>
            {availableTrades.map((trade: Trade) => (
              <SelectItem key={trade.id} value={trade.id.toString()}>
                {trade.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {availableTrades.length === 0 && selectedTrades.length === 0 && (
        <p className="text-sm text-gray-500">No trades available</p>
      )}
    </div>
  );
}
